import React from 'react';
import { DroneIcon } from 'components/icons';

const DEPLOYMENT_STEPS = [
  { label: 'Requesting deployment...', duration: 2000 },
  { label: 'Pilot acknowledged', duration: 2500 },
  { label: 'Drone coming online...', duration: 3000 },
  { label: 'Performing pre-flight checks...', duration: 2000 },
  { label: 'Launching drone', duration: 2500 },
];

export function DroneDeploymentStatus(props: {
  droneName: string;
  coordinates: string;
  onReady: () => void;
}): React.ReactElement {
  const { droneName, coordinates, onReady } = props;
  const [stepIndex, setStepIndex] = React.useState(0);

  React.useEffect(() => {
    if (stepIndex >= DEPLOYMENT_STEPS.length) {
      onReady();
      return undefined;
    }
    const timer = setTimeout(() => {
      setStepIndex((i) => i + 1);
    }, DEPLOYMENT_STEPS[stepIndex].duration);

    return (): void => clearTimeout(timer);
  }, [stepIndex, onReady]);

  const currentStep =
    DEPLOYMENT_STEPS[Math.min(stepIndex, DEPLOYMENT_STEPS.length - 1)];
  const progress = ((stepIndex + 1) / DEPLOYMENT_STEPS.length) * 100;

  return (
    <div className="odin-flex odin-flex-col odin-items-center odin-justify-center odin-h-full odin-w-full odin-gap-6 odin-bg-odin-black">
      <div className="odin-animate-pulse">
        <DroneIcon className="odin-h-16 odin-w-16 odin-text-purple-400" />
      </div>

      <div className="odin-flex odin-flex-col odin-items-center odin-gap-2">
        <span className="odin-text-lg odin-font-medium odin-text-white">
          {droneName}
        </span>
        <span className="odin-text-sm odin-text-slate-400">
          Deploying to {coordinates}
        </span>
      </div>

      <div className="odin-w-64 odin-flex odin-flex-col odin-gap-2">
        <div className="odin-h-1.5 odin-w-full odin-bg-white/20 odin-rounded-full odin-overflow-hidden">
          <div
            className="odin-h-full odin-bg-purple-500 odin-rounded-full odin-transition-all odin-duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="odin-text-sm odin-text-purple-300 odin-text-center">
          {currentStep.label}
        </span>
      </div>

      <div className="odin-flex odin-flex-col odin-gap-1 odin-mt-2">
        {DEPLOYMENT_STEPS.map((step, i) => (
          <div
            key={step.label}
            className="odin-flex odin-items-center odin-gap-2"
          >
            <div
              className={`odin-h-2 odin-w-2 odin-rounded-full ${
                // eslint-disable-next-line no-nested-ternary
                i < stepIndex
                  ? 'odin-bg-green-400'
                  : i === stepIndex
                  ? 'odin-bg-purple-400 odin-animate-pulse'
                  : 'odin-bg-slate-600'
              }`}
            />
            <span
              className={`odin-text-xs ${
                // eslint-disable-next-line no-nested-ternary
                i < stepIndex
                  ? 'odin-text-green-400'
                  : i === stepIndex
                  ? 'odin-text-white'
                  : 'odin-text-slate-600'
              }`}
            >
              {step.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
